import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { colors3 } from "../../colors/color";
import Printer2 from "../../components/Printer2";
import GraphLoader from "../../components/GraphLoader";
import { Grid, Button, ButtonGroup, Chip } from "@mui/material";
import { forecastingScbsmbStringDeviationAction } from "../../actions/predictiveAction";
import "./scbsmb.css";

const ScbSmbLossPred = ({
  selectedLebel,
  scbsmbDetail,
  databaseStr,
  title,
  setTitle,
}) => {
  const { forecastedScbsmbstringDeviation = [], scbsmbstringDeviationLoading } = useSelector((state) => state.forecasting);
  const { projectDetail } = useSelector((state) => state.projectDetail);

  /* ###   Initialize State to animate Graph Bars and Lines   ### */
  const [showEfficiencyYearlyLoss, setShowEfficiencyYearlyLoss] = useState({});
  const [tooltipAdjust, setTooltipAdjust] = useState({ x: null, y: null });
  const [scbSmbDeviation, setscbSmbDeviation] = useState(null);

  // State for time period selection
  const [selectedPeriod, setSelectedPeriod] = useState("historical");
  const [selectedFilter, setSelectedFilter] = useState("all");

  // Get the current date range type from selectedLebel
  const currentDateRangeType = selectedLebel?.dateRangeType || "month";

  // Dynamic time period options based on dateRangeType
  const getTimePeriods = () => {
    switch (currentDateRangeType) {
      case "5_min":
        return [
          { label: "Historical", value: "historical", description: "View past data (5-min)" },
          { label: "Next 1 Hour", value: "1hour", description: "Forecast next 1 hour" },
          { label: "Next 2 Hours", value: "2hours", description: "Forecast next 2 hours" },
        ];
      case "day":
        return [
          { label: "Historical", value: "historical", description: "View past data (daily)" },
          { label: "Next 7 Days", value: "7days", description: "Forecast next 7 days" },
          { label: "Next 30 Days", value: "30days", description: "Forecast next 30 days" },
        ];
      case "month":
        return [
          { label: "Historical", value: "historical", description: "View past data (monthly)" },
          { label: "Next 6 Months", value: "6months", description: "Forecast next 6 months" },
          { label: "Next Year", value: "year", description: "Forecast next year" },
        ];
      case "year":
        return [
          { label: "Historical", value: "historical", description: "View past data (yearly)" },
          { label: "Next 2 Years", value: "2years", description: "Forecast next 2 years" },
          { label: "Next 5 Years", value: "5years", description: "Forecast next 5 years" },
        ];
      default:
        return [
          { label: "Historical", value: "historical", description: "View past data" },
          { label: "Next 6 Months", value: "6months", description: "Forecast next 6 months" },
          { label: "Next Year", value: "year", description: "Forecast next year" },
        ];
    }
  };

  const timePeriods = getTimePeriods();

  // Filter options configuration
  const filterOptions = [
    { value: "all", label: "Show All" },
    { value: "2", label: "Deviation > 2%" },
    { value: "5", label: "Deviation > 5%" },
    { value: "10", label: "Deviation > 10%" }
  ];

  // Helper function to get slice limit based on period and date range type
  const getSliceLimit = (period) => {
    switch (period) {
      case "1hour": return 12;
      case "2hours": return 24;
      case "7days": return 7;
      case "30days": return 30;
      case "6months": return 6;
      case "year": return 12;
      case "2years": return 2;
      case "5years": return 5;
      default: return null;
    }
  };

  // Helper function to get forecast value based on selected period
  const getForecastValue = (period) => {
    switch (period) {
      case "1hour": return "1hour";
      case "2hours": return "2hours";
      case "historical": return "historical";
      case "7days": return "7days";
      case "30days": return "30days";
      case "6months": return "6months";
      case "year": return "year";
      case "2years": return "2years";
      case "5years": return "5years";
      default: return "historical";
    }
  };

  // Helper function to safely format numbers
  const formatNumber = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
      return null;
    }
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (typeof num !== 'number' || isNaN(num)) {
      return null;
    }
    return num.toFixed(2);
  };

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;

      return (
        <div
          style={{
            backgroundColor: "white",
            padding: "10px",
            border: "1px solid #ccc",
            borderRadius: "5px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
            minWidth: "200px",
          }}
        >
          <p style={{ margin: "0 0 5px 0", fontWeight: "bold" }}>
            {label}
          </p>
          {payload.map((entry, index) => {
            const hasNoData = entry.value === null || entry.value === undefined || entry.value === "N/A";
            return (
              <p
                key={index}
                style={{
                  margin: "2px 0",
                  color: entry.color,
                  fontSize: "0.8rem",
                  fontStyle: hasNoData ? "italic" : "normal",
                }}
              >
                {entry.name}: <strong>{hasNoData ? "No Data" : `${formatNumber(entry.value)}%`}</strong>
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };



  const graphRef = useRef(null);
  const dispatch = useDispatch();

  useEffect(() => {
    if (forecastedScbsmbstringDeviation) {
      let processedData;

      if (scbsmbDetail.scbsmbType === "SMB") {
        processedData = forecastedScbsmbstringDeviation?.map((e) => {
          let newObj = {};
          newObj["Date"] = e["Date"];
          let sum = 0, i = 0;
          Object.keys(e || {}).forEach((ee) => {
            if (ee !== "Date" && !isNaN(e[ee])) {
              sum += Number(e[ee]);
              i++;
            }
          });
          newObj[`${scbsmbDetail.scbsmbType}-${scbsmbDetail?.scbsmb?.split(" ")[1]}`] = Number(sum / i).toFixed(2);
          return newObj;
        }) || [];
      } else {
        processedData = forecastedScbsmbstringDeviation;
      }

      setscbSmbDeviation(processedData);

      let obj = {};
      const keys = [
        ...new Set(
          processedData?.flatMap(Object.keys).filter((e) => e !== "Date") || []
        ),
      ];
      keys.forEach((e) => {
        if (e !== "Date") {
          obj[e] = true;
        }
      });
      obj["all"] = true;
      setShowEfficiencyYearlyLoss(obj);
    }
  }, [forecastedScbsmbstringDeviation]);

  // Filter data based on selected time period and value filter
  const filteredScbSmbDeviation = useMemo(() => {
    if (!scbSmbDeviation || scbSmbDeviation.length === 0) return [];

    let periodFilteredData = [...scbSmbDeviation];

    if (selectedPeriod === "historical") {
      periodFilteredData = periodFilteredData.filter(item =>
        Object.keys(item).some(key => key !== "Date" && !key.includes("Forecast"))
      );
    } else {
      const sliceLimit = getSliceLimit(selectedPeriod);
      periodFilteredData = periodFilteredData.filter(item =>
        Object.keys(item).some(key => key !== "Date" && key.includes("Forecast"))
      );
      if (sliceLimit) {
        periodFilteredData = periodFilteredData.slice(0, sliceLimit);
      }
    }

    if (selectedFilter === "all") {
      return periodFilteredData;
    }

    const threshold = -parseFloat(selectedFilter);

    return periodFilteredData.map(item => {
      const filteredItem = { Date: item.Date };
      Object.keys(item).forEach(key => {
        
        if (key !== "Date" && !isNaN(item[key])) {
          const value = parseFloat(item[key]);
          
          if (value < threshold) {
            filteredItem[key] = item[key];
          } else {
            filteredItem[key] = null;
          }
        }
      });
      return filteredItem;
    });
  }, [scbSmbDeviation, selectedPeriod, selectedFilter]);

  useEffect(() => {
    let { inverter, block, scbsmb, scbsmbType, stack, sub_block } = scbsmbDetail;
    inverter = inverter?.split(" ")[1] || "ALL";
    block = block?.split(" ")[1];
    sub_block = sub_block?.split(" ")?.slice(-1);
    scbsmb = scbsmb?.split(" ")[1] === "SCB" || !scbsmb ? "ALL" : scbsmb?.split(" ")[1];
    stack = stack?.split(" ")[1];
    if (scbsmb) {
      setTitle(
        (projectDetail?.plantDetail?.block_name || "BL") +
        "-" + block + "-" +
        (sub_block ? projectDetail?.plantDetail?.sub_block_name + "-" + sub_block + "-" : "") +
        "INV-" + inverter +
        (stack ? "-STACK-" + stack : "") +
        "-" + scbsmbType + "-" + scbsmb
      );
    }
  }, [scbsmbDetail]);

  useEffect(() => {
    if (!selectedPeriod || !selectedLebel?.dateRangeType || !scbsmbDetail) return;

    const timer = setTimeout(() => {
      const timeRange = getForecastValue(selectedPeriod);
      dispatch(
        forecastingScbsmbStringDeviationAction(
          scbsmbDetail,
          { ...selectedLebel, forecast: timeRange },
          databaseStr
        )
      );
    }, 300);

    return () => clearTimeout(timer);
  }, [scbsmbDetail, selectedPeriod, selectedLebel?.dateRangeType, databaseStr, dispatch]);

  // Reset selected period when date range type changes
  useEffect(() => {
    setSelectedPeriod("historical");
  }, [currentDateRangeType]);

  const todayDate = new Date().toLocaleDateString();
  const startDate = selectedLebel?.startDate ? new Date(selectedLebel?.startDate).toLocaleDateString() : null;
  const endDate = selectedLebel?.endDate ? new Date(selectedLebel?.endDate).toLocaleDateString() : null;

  // Get unique keys for the chart
  const chartKeys = useMemo(() => {
    if (!filteredScbSmbDeviation?.length) return [];
    return [
      ...new Set(
        filteredScbSmbDeviation
          ?.flatMap(Object.keys)
          .filter((e) => e !== "Date") || []
      ),
    ].sort();
  }, [filteredScbSmbDeviation]);
  // Compute dynamic graph width
  const graphWidth = useMemo(() => {
    return filteredScbSmbDeviation?.length > 0
      ? filteredScbSmbDeviation?.length * chartKeys?.length * 50 + "px"
      : "100%";
  }, [filteredScbSmbDeviation, chartKeys]);

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
  };

  const handleFilterChange = (e) => {
    setSelectedFilter(e.target.value);
  };



  // Calculate min and max values including negatives
  const { minValue, maxValue } = useMemo(() => {
    if (!filteredScbSmbDeviation?.length) return { minValue: -10, maxValue: 10 };

    let minVal = Infinity;
    let maxVal = -Infinity;

    filteredScbSmbDeviation.forEach(item => {
      Object.keys(item).forEach(key => {
        if (key !== "Date" && !isNaN(item[key]) && item[key] !== null) {
          const val = parseFloat(item[key]);
          if (val < minVal) minVal = val;
          if (val > maxVal) maxVal = val;
        }
      });
    });

    // Set defaults if no values found
    if (minVal === Infinity) minVal = -10;
    if (maxVal === -Infinity) maxVal = 10;

    // Add padding for better visualization
    return {
      minValue: Math.floor(minVal) - 5,
      maxValue: Math.ceil(maxVal) + 5
    };
  }, [filteredScbSmbDeviation]);

  const totalCount = useMemo(() => scbSmbDeviation?.length || 0, [scbSmbDeviation]);
  const filteredCount = useMemo(() => filteredScbSmbDeviation?.length || 0, [filteredScbSmbDeviation]);

  const getDisplayUnit = () => {
    switch (currentDateRangeType) {
      case "5_min": return "5-min";
      case "day": return "Daily";
      case "month": return "Monthly";
      case "year": return "Yearly";
      default: return "Monthly";
    }
  };

  const getUnitLabel = () => {
    switch (currentDateRangeType) {
      case "5_min": return "intervals";
      case "day": return "days";
      case "month": return "months";
      case "year": return "years";
      default: return "months";
    }
  };

  const renderBarLabel = (value) => {
    if (value === null || value === undefined || isNaN(value)) return "";
    return formatNumber(value);
  };

  return (
    <>

      {/* Header with Time Period Selection */}
      <div
        style={{
          padding: "1rem 1.5rem",
          position: "sticky",
          left: "0",
          right: "0",
          top: "0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          textAlign: "center",
          zIndex: "10",
          backgroundColor: "white",
          flexWrap: "wrap",
          gap: "1rem",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "0.5rem" }}>
          <label style={{ fontSize: "0.9rem", fontWeight: 500, color: "#333" }}>
            Time Period ({getDisplayUnit()}):
          </label>
          <ButtonGroup variant="outlined" size="small" style={{ display: "flex", flexWrap: "wrap" }}>
            {timePeriods.map((period) => (
              <Button
                key={period.value}
                onClick={() => handlePeriodChange(period.value)}
                variant={selectedPeriod === period.value ? "contained" : "outlined"}
                style={{
                  backgroundColor: selectedPeriod === period.value ? "#1890ff" : "transparent",
                  color: selectedPeriod === period.value ? "white" : "#666",
                  borderColor: "#d0d0d0",
                  fontSize: "0.8rem",
                  padding: "4px 12px",
                  minWidth: "90px",
                }}
                title={period.description}
              >
                {period.label}
              </Button>
            ))}
          </ButtonGroup>
        </div>

        <div style={{ flex: 1, textAlign: "center" }}>
          <h3 style={{ margin: "0 0 5px 0", fontSize: "1rem" }}>
            {selectedPeriod === "historical" ? "Historical" : "Forecasted"} {scbsmbDetail.scbsmbType}(s) Normalized-STC Current Avg. Deviation (%) - {getDisplayUnit()} Data
            ({title || ""})
            {selectedFilter !== "all" && (
              <Chip
                label="Filtered"
                size="small"
                style={{
                  marginLeft: "8px",
                  backgroundColor: "#fff2e8",
                  color: "#fa541c",
                  borderColor: "#ffbb96",
                  fontSize: "0.7rem",
                  height: "20px",
                }}
                variant="outlined"
              />
            )}
          </h3>
        </div>

        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "0.5rem", justifyContent: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <label htmlFor="scb-smb-filter" style={{ fontSize: "0.85rem", fontWeight: 500, color: "#333" }}>
              Filter:
            </label>
            <select
              id="scb-smb-filter"
              value={selectedFilter}
              onChange={handleFilterChange}
              style={{
                padding: "0.4rem 0.8rem",
                border: "1px solid #d9d9d9",
                borderRadius: "4px",
                fontSize: "0.8rem",
                backgroundColor: "white",
                cursor: "pointer",
                minWidth: "160px",
              }}
            >
              {filterOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {selectedFilter !== "all" && totalCount > 0 && (
              <Chip
                label={`${filteredCount}/${totalCount} ${getUnitLabel()}`}
                size="small"
                style={{
                  fontSize: "0.7rem",
                  backgroundColor: "#e6f7ff",
                  color: "#1890ff",
                  borderColor: "#91d5ff",
                  height: "22px",
                }}
                variant="outlined"
              />
            )}
          </div>

            <Printer2
              dataSetDetail={{
                data: filteredScbSmbDeviation || [],
                name: `SCBSMB Deviation - ${getDisplayUnit()} (${selectedPeriod !== "historical" ? "Forecasted" : "Historical"})`,
                ref: graphRef,
                refCurrent: graphRef.current?.container,
              }}
            />
        </div>
      </div>

      {/* Custom Legend Container - Fixed Position */}
      {chartKeys.length > 0 && (
        <div
          style={{
            position: "sticky",
            left: "0",
            top: "85px",
            zIndex: "1",
            backgroundColor: "rgba(255, 255, 255, 0.98)",
            padding: "10px 16px",
           
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
             gap: "40px",
                  flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "20px", flexWrap: "wrap" }}>
            {chartKeys.map((key) => (
              <div
                key={key}
                onClick={() => {
                  setShowEfficiencyYearlyLoss(prev => ({
                    ...prev,
                    [key]: !prev[key]
                  }));
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                  userSelect: "none",
                  padding: "4px 8px",
                  borderRadius: "16px",
                  backgroundColor: showEfficiencyYearlyLoss[key] ? "transparent" : "#f5f5f5",
                  transition: "all 0.2s ease",
                }}
              >
                <div
                  style={{
                    width: "12px",
                    height: "12px",
                    backgroundColor: selectedPeriod === "historical" ? colors3[0] : "#203864",
                    borderRadius: "2px",
                    opacity: showEfficiencyYearlyLoss[key] ? 1 : 0.4,
                  }}
                />
                <span
                  style={{
                    fontSize: "0.7rem",
                    fontWeight: 500,
                    color: showEfficiencyYearlyLoss[key] ? "#333" : "#999",
                    textDecoration: showEfficiencyYearlyLoss[key] ? "none" : "line-through",
                  }}
                >
                  {key}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Graph Container */}
      <div
        style={{
          minWidth: "100%",
          width: scbsmbstringDeviationLoading ? "100%" : graphWidth,
          paddingTop: "10px",
          
        }}
        className="forecasting-scbsmb-container"
      >
        <ResponsiveContainer width="100%" height={"100%"}>
          {scbsmbstringDeviationLoading ? (
            <div style={{ position: "absolute", top: "0", bottom: "0", right: "0", left: "0", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <GraphLoader content={`${selectedPeriod !== "historical" ? "Forecasting" : "Loading"} String Deviation`} />
            </div>
          ) : filteredScbSmbDeviation?.length > 0 ? (
            <ComposedChart
              style={{ overflow: "hidden" }}
              onMouseMove={(e) => {
                if (e.isTooltipActive) {
                  setTooltipAdjust({
                    x: e.chartX > 20 ? e.chartX - 20 : e.chartX,
                    y: e.chartY > 150 ? 150 : e.chartY,
                  });
                }
              }}
              data={filteredScbSmbDeviation || []}
              margin={{ top: 15, right: 15, bottom: 15, left: 15 }}
              ref={graphRef}
            >
              <CartesianGrid stroke="grey" strokeWidth={0.3} />
              <XAxis
                fontSize={"0.8rem"}
                fontWeight={600}
                dataKey="Date"
                tickLine={false}
                axisLine={false}
                tickMargin={5}
                angle={-45}
                textAnchor="end"
                height={60}
                interval={0}
              />
              <YAxis
                type="number"
                fontSize={"0.8rem"}
                fontWeight={600}
                tickLine={false}
                tickMargin={5}
                tickCount={6}
                domain={[minValue, maxValue]}

                label={{
                  value: "String Current Deviation (%)",
                  dy:80,
                  angle: -90,
                  position: "insideLeft",
                  style: { fontSize: "0.8rem", fontWeight: "bold" },
                }}
              />
              <Tooltip
                position={tooltipAdjust}
                content={<CustomTooltip />}
                contentStyle={{ fontSize: "0.7rem" }}
              />
              <ReferenceLine y={0} stroke="#000" strokeWidth={1.5} />

              {chartKeys.map((e, index) => {
                if (e === "Date") return null;
                return (
                  <Bar
                    barSize={30}
                    key={index}
                    hide={showEfficiencyYearlyLoss[e] ? false : true}
                    dataKey={e}
                    fill={selectedPeriod === "historical" ? colors3[0] : "#203864"}
                 
                  >
                    <LabelList
                      dataKey={e}
                      position="top"
                      fontSize={"0.7rem"}
                      fontWeight={600}
                      fontFamily="Cambria, Cochin, Georgia, Times, 'Times New Roman', serif;"
                      formatter={renderBarLabel}
                    />
                  </Bar>
                );
              })}
            </ComposedChart>
          ) : (
            <div style={{ height: "100%", width: "100%", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: "1rem" }}>
              <h3>
                {selectedFilter !== "all"
                  ? "No data matches the filter criteria"
                  : scbSmbDeviation?.length === 0 && todayDate === startDate && todayDate === endDate
                    ? "Data Not Available On SCADA API"
                    : selectedPeriod === "historical"
                      ? "No data available for the selected time period"
                      : "No forecast data available for the selected time period"}
              </h3>
              {selectedFilter !== "all" && (
                <button
                  onClick={() => setSelectedFilter("all")}
                  style={{
                    padding: "0.5rem 1rem",
                    backgroundColor: "#1890ff",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "0.9rem",
                  }}
                >
                  Show All Data
                </button>
              )}
            </div>
          )}
        </ResponsiveContainer>
      </div>
    </>
  );
};

export default ScbSmbLossPred;